import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './Faq.scss';

const API_URL = process.env.REACT_APP_API_URL;

// FAQ step images are bare blob filenames (e.g. "FDRCable.png") living under the
// `images/` blob folder, served by GET /getImage?name=<file> — same convention as
// the instruction steps (see InstructionMenu.jsx).
export const imageUrl = (file) =>
  file ? `${API_URL}/getImage?name=${encodeURIComponent(file)}` : null;

// One chevron for every disclosure level — rotates 180° via the `.open` class on
// its container (see FAQ.scss). Decorative: hidden from assistive tech, which
// reads the button's aria-expanded instead.
export const Chevron = () => (
  <svg
    className="faq-chevron"
    viewBox="0 0 16 16"
    width="14"
    height="14"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M4 6l4 4 4-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// The /getFAQ endpoint returns top-level category keys (Download, Upload, …).
// Within each category the entries describe three nesting levels:
//   { header }    → begins a foldable item (the question)
//   { subheader } → begins a foldable sub-item nested inside the current item
//   { text, img } → a content step; it attaches to the current sub-item if one
//                   is open, otherwise directly to the current item
// This flattens into { title, items:[{ question, steps, subitems:[{ question,
// steps }] }] } — the shape the renderer consumes. Non-array categories are
// skipped, and text/subheaders appearing before any header still get a home.
export const adaptFAQ = (raw) => {
  if (!raw || typeof raw !== 'object') return [];
  return Object.entries(raw)
    .filter(([, entries]) => Array.isArray(entries))
    .map(([title, entries]) => {
      const items = [];
      let currentItem = null;
      let currentSub = null;

      const ensureItem = () => {
        if (!currentItem) {
          currentItem = { question: '', steps: [], subitems: [] };
          items.push(currentItem);
        }
        return currentItem;
      };

      entries.forEach((entry) => {
        if (!entry) return;
        if (entry.header !== undefined) {
          currentItem = { question: entry.header, steps: [], subitems: [] };
          currentSub = null;
          items.push(currentItem);
        } else if (entry.subheader !== undefined) {
          currentSub = { question: entry.subheader, steps: [] };
          ensureItem().subitems.push(currentSub);
        } else if (entry.text !== undefined) {
          const step = { text: entry.text, img: entry.img || null };
          if (currentSub) currentSub.steps.push(step);
          else ensureItem().steps.push(step);
        }
      });
      return { title, items };
    })
    .filter((section) => section.items.length > 0);
};

// Presentational accordion for a single set of adapted FAQ sections
// (sections › items › sub-items). Owns its own open/closed state, so multiple
// instances (e.g. one per company in the Support portal) fold independently.
// Consumers pass already-adapted `sections` (see adaptFAQ above).
const FaqAccordion = ({ sections = [], defaultSectionsOpen = true }) => {
  const [openSections, setOpenSections] = useState(() => {
    const state = {};
    if (defaultSectionsOpen) sections.forEach((_, i) => { state[i] = true; });
    return state;
  });
  const [openItems, setOpenItems] = useState({});

  const toggleSection = (sectionIdx) => {
    setOpenSections(prev => ({ ...prev, [sectionIdx]: !prev[sectionIdx] }));
  };

  const toggleItem = (sectionIdx, itemIdx) => {
    const key = `${sectionIdx}-${itemIdx}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleSubItem = (sectionIdx, itemIdx, subIdx) => {
    const key = `${sectionIdx}-${itemIdx}-${subIdx}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      {sections.map((section, sIdx) => (
        <div key={sIdx} className="faq-section-wrapper">

          {/* Section header — foldable */}
          <button
            className={`faq-section-header ${openSections[sIdx] ? 'open' : ''}`}
            onClick={() => toggleSection(sIdx)}
            aria-expanded={!!openSections[sIdx]}
          >
            <span>{section.title}</span>
            <Chevron />
          </button>

          {/* Section body */}
          {openSections[sIdx] && (
            <div className="faq-section-body">
              {(section.items || []).map((item, iIdx) => {
                const key = `${sIdx}-${iIdx}`;
                const isOpen = !!openItems[key];
                return (
                  <div key={iIdx} className={`faq-item ${isOpen ? 'open' : ''}`}>
                    <button
                      className="faq-question"
                      onClick={() => toggleItem(sIdx, iIdx)}
                      aria-expanded={isOpen}
                    >
                      <span>{item.question}</span>
                      <Chevron />
                    </button>
                    {isOpen && (
                      <div className="faq-answer">
                        {(item.steps || []).map((step, stepIdx) => (
                          <div key={stepIdx} className="faq-step">
                            {step.text && <p>{step.text}</p>}
                            {step.img && (
                              <img className="faq-step-img" src={imageUrl(step.img)} alt="" />
                            )}
                          </div>
                        ))}

                        {/* Nested foldable sub-items */}
                        {(item.subitems || []).map((sub, subIdx) => {
                          const subKey = `${sIdx}-${iIdx}-${subIdx}`;
                          const subOpen = !!openItems[subKey];
                          return (
                            <div
                              key={subIdx}
                              className={`faq-subitem ${subOpen ? 'open' : ''}`}
                            >
                              <button
                                className="faq-subquestion"
                                onClick={() => toggleSubItem(sIdx, iIdx, subIdx)}
                                aria-expanded={subOpen}
                              >
                                <span>{sub.question}</span>
                                <Chevron />
                              </button>
                              {subOpen && (
                                <div className="faq-subanswer">
                                  {(sub.steps || []).map((step, stepIdx) => (
                                    <div key={stepIdx} className="faq-step">
                                      {step.text && <p>{step.text}</p>}
                                      {step.img && (
                                        <img className="faq-step-img" src={imageUrl(step.img)} alt="" />
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

        </div>
      ))}
    </>
  );
};

FaqAccordion.propTypes = {
  sections: PropTypes.array,
  defaultSectionsOpen: PropTypes.bool,
};

export default FaqAccordion;
